import { useEffect } from "react";

const SUFFIX = "Frites Dorées";
const SITE_URL = "https://www.fritesdorees.fr";
const DEFAULT_TITLE = "Frites Dorées — Grossiste alimentaire B2B restauration rapide";
const DEFAULT_DESCRIPTION =
  "Grossiste alimentaire B2B spécialisé dans la restauration rapide : frites surgelées, viandes, sauces, emballages professionnels. Fournisseur fast-food et restauration rapide, livraison pros Île-de-France.";

const upsertMeta = (selector, attr, content) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, name] = selector.match(/\[(name|property)="([^"]+)"\]/) || [];
    if (selector.includes('name="')) {
      el.setAttribute("name", attr);
    } else if (selector.includes('property="')) {
      el.setAttribute("property", attr);
    }
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
};

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
};

/**
 * Per-route document head manager.
 *
 * - title: short page-specific phrase. We append " — Frites Dorées". Pass null for the brand default.
 * - description: 130-155 chars, B2B-oriented, written manually per page. Pass null for the site default.
 * - canonicalPath: pathname (`/contact`, `/catalogue?categorie=frites`, …). Pass null to fall back to current path.
 * - robots: "index" (default) or "noindex". Used by NotFound and the per-page "introuvable"
 *   states (invalid /produit/:slug, /concept/:slug, /blog/:slug) to keep Google from
 *   indexing soft-404 URLs as real pages.
 *
 * On unmount we restore the previous values so navigating back to a page
 * that doesn't set its own metadata doesn't leak the previous page's tags.
 */
export function useDocumentMeta({ title = null, description = null, canonicalPath = null, robots = "index" } = {}) {
  useEffect(() => {
    const prevTitle = document.title;

    const descEl = document.head.querySelector('meta[name="description"]');
    const prevDesc = descEl ? descEl.getAttribute("content") : null;

    const ogTitleEl = document.head.querySelector('meta[property="og:title"]');
    const prevOgTitle = ogTitleEl ? ogTitleEl.getAttribute("content") : null;

    const ogDescEl = document.head.querySelector('meta[property="og:description"]');
    const prevOgDesc = ogDescEl ? ogDescEl.getAttribute("content") : null;

    const ogUrlEl = document.head.querySelector('meta[property="og:url"]');
    const prevOgUrl = ogUrlEl ? ogUrlEl.getAttribute("content") : null;

    const canonicalEl = document.head.querySelector('link[rel="canonical"]');
    const prevCanonical = canonicalEl ? canonicalEl.getAttribute("href") : null;

    const robotsEl = document.head.querySelector('meta[name="robots"]');
    const prevRobots = robotsEl ? robotsEl.getAttribute("content") : null;

    // Apply new values
    const fullTitle = title ? `${title} — ${SUFFIX}` : DEFAULT_TITLE;
    document.title = fullTitle;

    const fullDesc = description || DEFAULT_DESCRIPTION;
    upsertMeta('meta[name="description"]', "description", fullDesc);
    upsertMeta('meta[property="og:title"]', "og:title", fullTitle);
    upsertMeta('meta[property="og:description"]', "og:description", fullDesc);

    const path =
      canonicalPath != null
        ? canonicalPath
        : typeof window !== "undefined"
          ? window.location.pathname
          : "/";
    const cleanPath = path.split("?")[0] || "/";
    const fullCanonical = `${SITE_URL}${cleanPath === "/" ? "/" : cleanPath}`;
    upsertCanonical(fullCanonical);
    upsertMeta('meta[property="og:url"]', "og:url", fullCanonical);

    // Robots: only override when explicitly noindex. Leave the static
    // "index, follow" from index.html alone for normal pages — we don't
    // want to spam every route with a redundant tag.
    if (robots === "noindex") {
      upsertMeta('meta[name="robots"]', "robots", "noindex, follow");
    } else if (prevRobots && prevRobots.includes("noindex")) {
      // restore the index default if a previous route set noindex
      upsertMeta('meta[name="robots"]', "robots", "index, follow");
    }

    return () => {
      document.title = prevTitle;
      if (prevDesc !== null) upsertMeta('meta[name="description"]', "description", prevDesc);
      if (prevOgTitle !== null) upsertMeta('meta[property="og:title"]', "og:title", prevOgTitle);
      if (prevOgDesc !== null) upsertMeta('meta[property="og:description"]', "og:description", prevOgDesc);
      if (prevOgUrl !== null) upsertMeta('meta[property="og:url"]', "og:url", prevOgUrl);
      if (prevCanonical !== null) upsertCanonical(prevCanonical);
      if (prevRobots !== null) upsertMeta('meta[name="robots"]', "robots", prevRobots);
    };
  }, [title, description, canonicalPath, robots]);
}
